import React, { useEffect, useMemo, useState } from 'react';
import TimeRowGroup from '../types/TimePeriodGroupM';
import { PeriodDay } from '../types/PeriodDay';
import MyTimeAdd from './MyTimeAdd';
import Cell from './Cell';
import TotalCell from './TotalCell';
import { TimeGridMode } from '../types/TimeGridMode';
import { DirectionalHint, Stack, Text, TooltipHost, FontIcon } from '@fluentui/react';
import '../Styles/TimePeriod.scss';
import {
    GetTotalFromTimeRowInDayArray,
    GetTotalFromTimeEntriesArray,
    IsDatesNoTimeEqual,
    FormatValue,
    MathToFixed,
    IsWeekend,
    IsHoliday,
    IsDeadline,
} from './Utils';
import { WorkTypePicker } from './WorkTypePicker';
import MyTimeDelete from './MyTimeDelete';
import { useCellCtx } from './Contexts/CellContext';
import ProjectSourceIcon from '../Icons/ProjectSourceIcon';
import { DisplayCell } from './WeekOverview/DisplayCell';
import { EditableRemainingWorkCell } from './WeekOverview/EditableRemainingWorkCell';
import { PlannedCell } from './PlannedCell';
import { Allocation, TimeEntry, UserRegistrationPeriod, UserReportingPeriodState, WorkType } from '../api/generated/data-contracts';
import { useGridContext } from './Contexts/GridContext/context';
import { useConfigValuesState } from '../hooks/useConfigValue';
import { useIsCellReadOnly } from '../helpers/cellReadOnly';
import { RowAllocationCell } from './RowAllocationCell';
import { RowTotalCell } from './RowTotalCell';
import { calculateDisplayedRemainingWork } from './WeekOverview/RemainingWorkCalculation';

interface propsTimePeriodGroup {
    group: TimeRowGroup;
    expanded: boolean;
    onCellUpdated: (
        time: UserRegistrationPeriod,
        day: PeriodDay,
        te: TimeEntry,
        value: number,
        comment: string,
        flagged: boolean,
        mode: TimeGridMode,
        previousValue: number,
        saveSuccess: Function,
        skipApiCall?: boolean,
    ) => void;
    totalCellUpdated: Function;
    mode: TimeGridMode;
    pinMyTime: Function;
    deleteMyTime: Function;
    today: Date;
    submitted: boolean;
    periodIsClosed: boolean;
    timeLowerLimitConfig: string;
    startDate: Date;
    endDate: Date;
    deadlineDate: Date;
    perioddays: PeriodDay[];
    reportPeriodId: string;
    updateWorkType: (row: UserRegistrationPeriod, workType: WorkType, changeCompleted: Function) => void;
    allocations: Allocation[];
    showAllocations: boolean;
    state: UserReportingPeriodState;
    recalculateActualWork?: (assignmentId: string) => void;
    onRemainingWorkUpdate?: (assignmentId: string, newRemainingWork: number) => void;
    allTimes: UserRegistrationPeriod[];
}

function TimePeriodGroup(props: propsTimePeriodGroup) {
    const [times, setTimes] = useState<UserRegistrationPeriod[]>([]);
    const [expanded, setExpanded] = useState<boolean>(true);
    const [expandMiddleman, setExpandMiddleman] = useState<boolean>(true);
    const [groupiconrotation, setGroupiconrotation] = useState<string>('rotate(-90deg)');

    let timesheetIsReadonly = false;

    if (props.submitted || props.periodIsClosed) timesheetIsReadonly = true;
    if (props.state === 'Rejected') timesheetIsReadonly = false;

    const [cellDataNeedsSave, setCellDataNeedsSave] = useState<boolean>(false);

    const [updateTotal, setUpdateTotal] = useState<boolean>(true);

    const cellCtx = useCellCtx();

    const { allocations } = props;

    const { plannedActive, actualActive, remainingActive } = useGridContext();
    const {
        initialData: {
            configuration: {
                timeSheetValidation: { timeEntriesMustHaveWorkType },
                workTypesEnabled,
                onlyAllowTimeSheetWithinTaskStartEnd,
                showPlanned,
                timeEntryRoundingInterval,
                weekConfig,
                highlightAllocatedProjects,
                remainingWorkWritable,
            },
            aggregatedUser,
            allowedWorkTypes,
            projectTypes,
            calendarHolidays,
        },
    } = useConfigValuesState();

    const userHolidays = useMemo(() => calendarHolidays.map(m => new Date(m)), [calendarHolidays]);

    const { getIfReadOnly } = useIsCellReadOnly({
        timesheetIsReadonly,
        onlyAllowTimeSheetWithinTaskStartEnd,
        periodDays: props.perioddays,
        aggregatedUser,
        workTypesEnabled,
        timeEntriesMustHaveWorkType,
        allowedWorkTypes,
        projectTypes,
    });

    useEffect(() => {
        setTimes([...props.group.TimeRows]);
    }, [props.group.TimeRows, updateTotal]);

    useEffect(() => {
        // setExpanded(props.expanded);
        setExpandMiddleman(props.expanded);
    }, [props.expanded]);

    useEffect(() => {
        if (expanded) {
            setGroupiconrotation('rotate(0deg)');
        } else {
            setGroupiconrotation('rotate(-90deg)');
        }
    }, [expanded]);

    useEffect(() => {
        if (!cellDataNeedsSave) {
            setExpanded(expandMiddleman);
        }
    }, [expandMiddleman, cellDataNeedsSave]);

    const groupGollapsedExpanded = (ev: React.MouseEvent<HTMLTableRowElement, MouseEvent>): void => {
        setExpandMiddleman(!expandMiddleman);
    };

    const onCellUpdated = (
        time: UserRegistrationPeriod,
        day: PeriodDay,
        te: TimeEntry,
        value: number,
        comment: string,
        flagged: boolean,
        mode: TimeGridMode,
        previousValue: number,
        saveSuccess: Function,
        skipApiCall?: boolean,
    ): void => {
        // Don't update local state here - let the parent component handle the state updates
        // and rely on the useEffect to sync the updated data from props.group.TimeRows
        props.onCellUpdated(time, day, te, value, comment, flagged, mode, previousValue, saveSuccess, skipApiCall);
    };

    const PinMyTime = (time: UserRegistrationPeriod, pinned: boolean) => {
        props.pinMyTime(time, pinned);
    };

    const DeleteMyTime = (time: UserRegistrationPeriod) => {
        props.deleteMyTime(time);
    };

    const setCellDataNeedsSaveFunc = (val: boolean) => {
        setCellDataNeedsSave(val);
    };

    const handleRemainingWorkUpdate = (assignmentId: string, newRemainingWork: number) => {
        // Update local state
        setTimes(currentTimes => currentTimes.map(time => (time.assignmentId === assignmentId ? { ...time, remainingWork: newRemainingWork } : time)));

        // Notify parent component to update its state as well
        if (props.onRemainingWorkUpdate) {
            props.onRemainingWorkUpdate(assignmentId, newRemainingWork);
        }
    };

    const allocationsForProject = allocations
        .filter(a => a.projectId === props.group.GroupId)
        .map(a => a.hours)
        .reduce((acc, value) => acc + value, 0);

    const hasHighlightedAllocations = useMemo(
        () => allocationsForProject > 0 && highlightAllocatedProjects,
        [allocationsForProject, highlightAllocatedProjects],
    );

    return (
        <>
            <tr
                onClick={groupGollapsedExpanded}
                className={`groupHeadRow${props.today === props.startDate ? ' isMonday' : ''}  ${hasHighlightedAllocations ? ' hasAllocations' : ''}`}
            >
                <td className="rowHeader noselect">
                    <Stack horizontal verticalAlign="center" className="rowHeaderContainer">
                        <Stack.Item className="expander">
                            <div className="expanderInner">
                                {/* <Stack.Item className={`expander${props.expanded ? " activePin" : ""}`}> */}
                                <FontIcon
                                    iconName="ChevronDown"
                                    style={{
                                        transform: groupiconrotation,
                                        transition: 'transform .2s ease 0s',
                                    }}
                                />
                            </div>
                        </Stack.Item>
                        <Text className="groupName textOverflow">
                            {props.group.GroupName}
                            {hasHighlightedAllocations && (
                                <div className="tp-glimmer-container">
                                    <TooltipHost content="This project has allocations" directionalHint={DirectionalHint.topCenter}>
                                        <FontIcon iconName="Glimmer" className="tp-glimmer-icon" aria-hidden="true" />
                                    </TooltipHost>
                                </div>
                            )}
                        </Text>
                        {props.group?.ProjectSource?.iconUrl && (
                            <Stack.Item>
                                <ProjectSourceIcon
                                    id={`${props.group.GroupName}_${props.group.ProjectSource.name}`}
                                    projectSource={props.group.ProjectSource}
                                />
                            </Stack.Item>
                        )}
                    </Stack>
                </td>
                <td className="cellContainer worktypeContainer noselect" hidden={!workTypesEnabled}>
                    <Stack horizontal className="cell day">
                        <Text className="groupName textOverflow"> {/* White space for work type (Strange layout if empty...) */}</Text>
                    </Stack>
                </td>
                {plannedActive && showPlanned && (
                    <td className={`rowTotalContainer noselect`}>
                        <div className="rowTotal totalCell">
                            <Text className="totalCellInner" as="div">
                                {remainingWorkWritable ? (
                                    <PlannedCell
                                        time={{
                                            actualWork: times.reduce((prev, cur) => prev + (cur.actualWork || 0), 0),
                                            remainingWork: times.reduce((prev, cur) => prev + (cur.remainingWork || 0), 0),
                                            plannedWork: times.reduce((prev, cur) => prev + (cur.plannedWork || 0), 0),
                                        }}
                                        periodDays={props.perioddays}
                                        useDisplayCell={true}
                                    />
                                ) : (
                                    <Text className="cellInner parCellInner" as="div">
                                        {times.reduce((prev, cur) => prev + (cur.plannedWork || 0), 0) <= 0
                                            ? null
                                            : MathToFixed(
                                                  times.reduce((prev, cur) => prev + (cur.plannedWork || 0), 0),
                                                  2,
                                              )}
                                    </Text>
                                )}
                            </Text>
                        </div>
                    </td>
                )}
                {actualActive && showPlanned && (
                    <td className={`rowTotalContainer noselect`}>
                        <div className="rowTotal totalCell">
                            <Text className="totalCellInner" as="div">
                                <DisplayCell
                                    value={MathToFixed(
                                        times.reduce((prev, cur) => prev + (cur.actualWork || 0), 0),
                                        2,
                                    )}
                                    periodDays={props.perioddays}
                                />
                            </Text>
                        </div>
                    </td>
                )}
                {remainingActive && showPlanned && (
                    <td className={`rowTotalContainer noselect`}>
                        <div className="rowTotal totalCell">
                            <Text className="totalCellInner" as="div">
                                <DisplayCell
                                    value={MathToFixed(
                                        times.reduce((prev, cur) => prev + (cur.remainingWork || 0), 0),
                                        2,
                                    )}
                                    periodDays={props.perioddays}
                                />
                            </Text>
                        </div>
                    </td>
                )}
                {props.perioddays.map((date: PeriodDay, index) => (
                    <td
                        key={date.id}
                        className={`rowTotalContainer noselect${IsDatesNoTimeEqual(props.today, date.date) ? ' today' : ''}${
                            IsDeadline(date.date, props.deadlineDate) ? ' deadline' : ''
                        }${IsHoliday(date.date, userHolidays) ? ' holiday' : ''}`}
                    >
                        <div className="rowTotal totalCell">
                            <Text className="totalCellInner" as="div">
                                {FormatValue(Number(GetTotalFromTimeRowInDayArray(times, date.date) || ''))}
                            </Text>
                        </div>
                    </td>
                ))}
                <td className="rowTotalContainer noselect">
                    <div className="rowTotal totalCell">
                        <Text as="div" className="totalCellInner">
                            <RowTotalCell projectId={props.group.GroupId} times={times} allocation={allocationsForProject} />
                        </Text>
                    </div>
                </td>
                {props.showAllocations && (
                    <td className="rowTotalContainer noselect">
                        <div className="rowTotal totalCell">
                            <RowAllocationCell projectId={props.group.GroupId} allocation={allocationsForProject} />
                        </div>
                    </td>
                )}
            </tr>
            {times.map((time, index) => {
                if (!expanded) {
                    return null;
                }

                return (
                    <tr
                        key={time.assignmentId}
                        className={`subRow${props.today === props.startDate ? ' isMonday' : ''}${index === times.length - 1 ? ' lastSubRow' : ''}`}
                    >
                        <td className="rowHeader noselect">
                            <Stack horizontal className="rowHeaderContainer">
                                <div className="expander" />
                                <Stack.Item grow>
                                    <TooltipHost
                                        directionalHint={DirectionalHint.topLeftEdge}
                                        content={
                                            time.taskName.length < 70
                                                ? time.taskDescription
                                                : time.taskName + (time.taskDescription ? ' - ' + time.taskDescription : '')
                                        }
                                        closeDelay={250}
                                    >
                                        <Text className="subGroupName textOverflow">
                                            {time.taskName.length < 70 ? time.taskName : time.taskName.substr(0, 68) + '...'}
                                        </Text>
                                    </TooltipHost>
                                </Stack.Item>

                                <Stack.Item className="noselect rowDelete">
                                    <MyTimeDelete Time={time} deleteMyTime={DeleteMyTime} enabled={!timesheetIsReadonly} />
                                </Stack.Item>

                                <Stack.Item className="noselect pin">
                                    <MyTimeAdd key={index} pinned={time.pinned} Time={time} pinMyTime={PinMyTime} isMyTime={true} />
                                </Stack.Item>
                            </Stack>
                        </td>

                        {workTypesEnabled && (
                            <td className="cellContainer worktypeContainer noselect">
                                <div className="cell day">
                                    <WorkTypePicker
                                        readOnly={timesheetIsReadonly || time.projectClosed || time.taskClosed}
                                        row={time}
                                        existingTaskTimeRows={times}
                                        onChange={props.updateWorkType}
                                    />
                                </div>
                            </td>
                        )}

                        {plannedActive && showPlanned && (
                            <td className={`cellContainer parContainer noselect`}>
                                <div className="cell parCell day">
                                    {remainingWorkWritable ? (
                                        <PlannedCell time={time} />
                                    ) : (
                                        <Text className="cellInner parCellInner" as="div">
                                            {(time.plannedWork || 0) <= 0 ? null : MathToFixed(time.plannedWork, 2)}
                                        </Text>
                                    )}
                                </div>
                            </td>
                        )}
                        {actualActive && showPlanned && (
                            <td className={`cellContainer parContainer noselect`}>
                                <div className="cell parCell day">
                                    <Text className="cellInner parCellInner" as="div">
                                        {(time.actualWork || 0) <= 0 ? null : MathToFixed(time.actualWork, 2)}
                                    </Text>
                                </div>
                            </td>
                        )}
                        {remainingActive && showPlanned && (
                            <td className={`cellContainer parContainer noselect`}>
                                <div className="cell parCell day">
                                    {/* If not a default task, allow editing of remaining work */}
                                    {remainingWorkWritable && !time.taskIsDefault ? (
                                        <EditableRemainingWorkCell
                                            assignmentId={time.assignmentId}
                                            taskId={time.taskId}
                                            workTypeId={time.workType?.id}
                                            value={time.remainingWork || 0}
                                            periodDays={props.perioddays}
                                            readonly={timesheetIsReadonly || time.projectClosed || time.taskClosed || !remainingWorkWritable}
                                            onRemainingWorkUpdate={handleRemainingWorkUpdate}
                                        />
                                    ) : (
                                        <Text className="cellInner parCellInner" as="div">
                                            {calculateDisplayedRemainingWork(time)}
                                        </Text>
                                    )}
                                </div>
                            </td>
                        )}

                        {props.perioddays.map((day: PeriodDay, index) => {
                            const lowercasedDayName = day.translatedName.toLowerCase();
                            const isDayEnabled = weekConfig.find(day => day.name === lowercasedDayName)?.enabled ?? false;

                            // Collect all time entries for this specific day across ALL projects/tasks
                            const allTimeEntriesForDay = props.allTimes.flatMap(
                                timeRow =>
                                    timeRow.timeEntries?.filter(te => new Date(te.date).getISOStringMidnight() === day.date.getISOStringMidnight()) || [],
                            );

                            return (
                                <td
                                    key={day.id}
                                    className={`cellContainer noselect${IsWeekend(day.date) || !isDayEnabled ? ' weekend' : ''}${
                                        IsDeadline(day.date, props.deadlineDate) ? ' deadline' : ''
                                    }${IsHoliday(day.date, userHolidays) ? ' holiday' : ''}${IsDatesNoTimeEqual(props.today, day.date) ? ' today' : ''}`}
                                >
                                    <div className="cell day" data-testid={`day-${day.originalName} ${day.id < 5 ? 'weekdays' : 'weekends'}`}>
                                        <Cell
                                            time={time}
                                            timeentry={
                                                time.timeEntries?.filter(te => new Date(te.date).getISOStringMidnight() === day.date.getISOStringMidnight())[0]
                                            } //[0] @kra is risky - please improve to check for existance of a TimeEntry -> I think this is fine, we just need to handle undefined in Celll
                                            // cju: by doing that, you are just sending an error further into the app, instead of handling it here, which will do it more complex to read and understand the code.
                                            // you know timeEntries is an array, so add '?? []' to the end, would fix it here, instead of timeentry have the possibility of being undefined
                                            day={day}
                                            updated={onCellUpdated}
                                            readonlyreason={getIfReadOnly(time, day)}
                                            timeLowerLimitConfig={props.timeLowerLimitConfig}
                                            ref={n => cellCtx.addInputNode(time.taskId, time.workType?.id, index, n)}
                                            cellDataNeedsSave={setCellDataNeedsSaveFunc}
                                            allTimeEntriesForDay={allTimeEntriesForDay}
                                        />
                                    </div>
                                </td>
                            );
                        })}
                        <td className="rowTotalContainer totalRow noselect">
                            <div className="rowTotal totalCell" data-testid="day-total">
                                <TotalCell
                                    initialValue={MathToFixed(GetTotalFromTimeEntriesArray(time.timeEntries), 2)}
                                    periodDays={props.perioddays}
                                    readonly={getIfReadOnly(time) !== ''}
                                    updated={onCellUpdated}
                                    timeRow={time}
                                    recalculateActualWork={props.recalculateActualWork}
                                    timeLowerLimitConfig={String(timeEntryRoundingInterval)}
                                    allTimes={props.allTimes}
                                />
                            </div>
                        </td>
                    </tr>
                );
            })}
        </>
    );
}

export default TimePeriodGroup;
